import { useState, useCallback } from "react";
import type { HierarchicalItem, HierarchyApiService } from "../../../types/uiTypes";

type UseDragAndDropProps<T extends HierarchicalItem> = {
  enabled: boolean;
  apiService: HierarchyApiService<T>;
  singularName: string;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  refetch: () => void;
};

export function useDragAndDrop<T extends HierarchicalItem>({
  enabled,
  apiService,
  singularName,
  onSuccess,
  onError,
  refetch,
}: UseDragAndDropProps<T>) {
  const [draggedItem, setDraggedItem] = useState<T | null>(null);
  const [dragOverItem, setDragOverItem] = useState<T | null>(null);
  const [dragModal, setDragModal] = useState<{
    draggedItem: T;
    targetItem: T;
  } | null>(null);
  const [dragErrorModal, setDragErrorModal] = useState<string | null>(null);

  const handleDragStart = useCallback(
    (item: T) => {
      if (enabled) {
        setDraggedItem(item);
      }
    },
    [enabled],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, item: T) => {
      if (!enabled) return;

      e.preventDefault();
      e.stopPropagation();
      if (draggedItem && draggedItem.id !== item.id) {
        setDragOverItem(item);
      }
    },
    [enabled, draggedItem],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent, targetItem: T) => {
      if (!enabled || !apiService.move) return;

      e.preventDefault();
      e.stopPropagation();

      if (!draggedItem || draggedItem.id === targetItem.id) {
        setDraggedItem(null);
        setDragOverItem(null);
        return;
      }

      // Check if dropping onto self or a descendant
      const isDescendant = (parent: T, childId: string): boolean => {
        if (parent.id === childId) return true;
        if (!parent.children) return false;
        return parent.children.some(child => isDescendant(child as T, childId));
      };

      if (isDescendant(draggedItem, targetItem.id)) {
        setDragErrorModal(
          "Cannot move parent into a child. Remove all children first and try again.",
        );
        setDraggedItem(null);
        setDragOverItem(null);
        return;
      }

      // Show confirmation modal
      setDragModal({ draggedItem, targetItem });
      setDragOverItem(null);
    },
    [enabled, apiService.move, draggedItem],
  );

  const handleConfirmDrag = useCallback(async () => {
    if (!dragModal || !apiService.move) return;

    try {
      await apiService.move(dragModal.draggedItem.id, dragModal.targetItem.id);
      setDragModal(null);
      setDraggedItem(null);
      refetch();
      onSuccess(`${singularName} moved successfully`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      onError(`Failed to move: ${message}`);
    }
  }, [dragModal, apiService, refetch, onSuccess, onError, singularName]);

  const handleCloseDragModal = useCallback(() => {
    setDragModal(null);
    setDraggedItem(null);
  }, []);

  const handleCloseDragErrorModal = useCallback(() => {
    setDragErrorModal(null);
  }, []);

  return {
    draggedItem,
    dragOverItem,
    dragModal,
    dragErrorModal,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleConfirmDrag,
    handleCloseDragModal,
    handleCloseDragErrorModal,
  };
}
